import { Inject, Injectable, Optional } from '@angular/core';
import { FhirApiService } from '../../services/api/fhir-api.sevice';
import { QuestionnaireEntryAdapter } from './questionnaire-entry.adapter';

import { DataFhirService } from '../data-fhir.service';
import { HttpParamsObject } from '../../services/api/api.service';
import { UserService } from '../../services/user.service';
import {
  catchError,
  concatMap,
  mergeMap,
  switchMap,
  tap,
} from 'rxjs/operators';
import { Observable } from 'rxjs/internal/Observable';
import { combineLatest, of } from 'rxjs';
import { ID } from '../data.model';
import { QuestionnaireResponseService } from './questionnaire-repsonse/questionnaire-respone.service';
import { TranslocoService } from '@jsverse/transloco';
import { ReducerManager, Store } from '@ngrx/store';
import { QuestionnaireEntry } from '@core/data/questionnaires/questionnaire-entry.model';
import { QuestionnaireResponseEntry } from './questionnaire-repsonse/questionnaire-response.model';

type StatusColor = 'light-blue' | 'yellow' | 'green' | 'red' | 'white' | 'grey';

@Injectable({ providedIn: 'root' })
export class QuestionnairesService extends DataFhirService<QuestionnaireEntry> {
  protected readonly defaultParamsObject: HttpParamsObject = {
    _sort: '-_lastUpdated',
    _include: '*',
    'code:missing': true,
  };

  private readonly statusColorMap: Record<string, StatusColor> = {
    active: 'yellow',
    completed: 'light-blue',
    'on-hold': 'yellow',
  };

  constructor(
    @Inject('dataName') @Optional() dataName: string,
    protected adapter: QuestionnaireEntryAdapter,
    protected fhirApi: FhirApiService,
    protected userService: UserService,
    protected store: Store,
    protected manager: ReducerManager,
    protected questionnaireResponseService?: QuestionnaireResponseService,
    private translocoService?: TranslocoService,
  ) {
    super(
      dataName || 'questionnaires',
      adapter,
      fhirApi,
      userService,
      store,
      manager,
    );
  }

  fetchData(paramsObject?: HttpParamsObject, selectedIds?: ID[]): void {
    this.setLoading(true);

    this.searchItems$(paramsObject)
      .pipe(
        mergeMap(
          (entries: QuestionnaireEntry[]): Observable<QuestionnaireEntry[]> => {
            if (!entries.length) return of([]);

            return combineLatest(
              entries.map(
                (entry: QuestionnaireEntry): Observable<QuestionnaireEntry> => {
                  const questionaryReference =
                    entry?.['supportingInfo']?.[0].reference ||
                    (entry.questionnaireId
                      ? `Questionnaire/${entry.questionnaireId}`
                      : null);

                  return questionaryReference
                    ? this.fhirApi.read('', questionaryReference).pipe(
                        switchMap(
                          (detail): Observable<QuestionnaireEntry> =>
                            this.adapter.fromResource$(entry, detail),
                        ),
                        catchError((err) => of(null)),
                      )
                    : of(null);
                },
              ),
            );
          },
        ),
      )
      .subscribe(
        (dataList) => {
          dataList = dataList.filter((x) => x != undefined);
          this.currentFetchParams = paramsObject;

          this.setItems(dataList);
          this.setActive(selectedIds);
        },
        (err) => this.setError(err),
        () => this.setLoading(false),
      );
  }

  saveQuestionnaireResponseAndServiceRequest$(data: {
    questionnaireResponseId: string;
    serviceRequestId: string;
    status: string;
  }): Observable<any> {
    this.setLoading(true);
    const { questionnaireResponseId, serviceRequestId, status } = data;

    const disableCache = true; // Disable cache for getting ServiceRequest and QuestionnaireResponse for renderer

    return combineLatest([
      this.questionnaireResponseService.getItemById$(
        questionnaireResponseId,
        disableCache,
      ),
      this.getItemById$(serviceRequestId, disableCache),
    ]).pipe(
      concatMap(([questionnaireResponse, serviceRequest]) => {
        const updatedResource: QuestionnaireResponseEntry = {
          ...questionnaireResponse,
          subject: serviceRequest._resource?.subject,
          basedOn: questionnaireResponse.basedOn ?? [
            {
              reference: `ServiceRequest/${serviceRequestId}`,
            },
          ],
        };

        const updatedServiceRequest: QuestionnaireEntry = {
          ...serviceRequest,
          status: status === 'draft' ? 'on-hold' : 'completed',
        };

        return this.questionnaireResponseService
          .updateItem$(updatedResource)
          .pipe(
            concatMap(() => {
              this.setLoading(false);
              return this.updateItem$(updatedServiceRequest).pipe(
                tap(() => {
                  this.updateStoreItem(
                    updatedServiceRequest.id,
                    updatedServiceRequest,
                  );
                }),
              );
            }),
            catchError((err) => {
              this.setLoading(false);
              throw err;
            }),
          );
      }),
    );
  }

  deleteQuestionnaire$(eventToDelete): Observable<any> {
    const params = { _cascade: 'delete' };
    return this.deleteItemById$(eventToDelete.id, params);
  }

  getStatusTranslations(status: string): string {
    const key = `app.documents.questionnaires.${status}`;
    return this.translocoService.translate(key);
  }

  getStatusColor(status: string): StatusColor {
    return this.statusColorMap[status];
  }
}
